import { useEffect, useState } from "react";
import {
    Edit2, Eye, Upload, Plus, X, Sparkles, BookOpen,
    Layers, FileText, CheckCircle2, Lock, Unlock, AlertCircle,
    ClipboardList, Trash2, Users,
} from "lucide-react";
import { api } from "../../lib/api";
import LearningStyleBadge, { STYLE_META } from "../../components/LearningStyleBadge";
import { toast } from "sonner";

const BASE_GRADES = [
    "1ro Primaria", "2do Primaria", "3ro Primaria", "4to Primaria", "5to Primaria", "6to Primaria",
    "1ro Secundaria", "2do Secundaria", "3ro Secundaria", "4to Secundaria", "5to Secundaria",
];
const SECTIONS = ["A", "B", "C"];
const GRADES = BASE_GRADES.flatMap((g) =>
    g === "5to Secundaria"
        ? ["A", "B", "C"].map((s) => `${g} ${s}`)
        : ["A", "B"].map((s) => `${g} ${s}`)
);
const UNIT_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const DELIVERY_TYPE_LABELS = {
    pdf: "Archivo PDF",
    photo: "Foto",
    text: "Texto escrito",
};

export default function MisClases() {
    const [tab, setTab] = useState("lecciones");
    return (
        <div className="p-6 md:p-10 fade-in" data-testid="mis-clases-page">
            <div className="mb-6">
                <h1 className="text-3xl font-semibold">Mis Clases</h1>
                <p className="text-edu-body">Gestiona unidades, lecciones, materiales y exámenes.</p>
            </div>
            <div className="flex gap-2 mb-6 border-b border-edu" role="tablist">
                <TabBtn active={tab === "lecciones"} onClick={() => setTab("lecciones")} icon={BookOpen} label="Lecciones" testId="tab-lecciones" />
                <TabBtn active={tab === "unidades"} onClick={() => setTab("unidades")} icon={Layers} label="Unidades" testId="tab-unidades" />
                <TabBtn active={tab === "examenes"} onClick={() => setTab("examenes")} icon={FileText} label="Exámenes" testId="tab-examenes" />
            </div>
            {tab === "lecciones" && <LessonsTab />}
            {tab === "unidades" && <UnitsTab />}
            {tab === "examenes" && <ExamsTab />}
        </div>
    );
}

function TabBtn({ active, onClick, icon: Icon, label, testId }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            role="tab"
            aria-selected={active}
            className={`px-4 py-2.5 text-sm font-medium inline-flex items-center gap-2 border-b-2 -mb-px transition ${
                active ? "border-edu-navy text-edu-navy" : "border-transparent text-edu-muted hover:text-edu-text"
            }`}
        >
            <Icon size={14} /> {label}
        </button>
    );
}

/* =================== LESSONS TAB =================== */
function LessonsTab() {
    const [lessons, setLessons] = useState([]);
    const [allLessons, setAllLessons] = useState([]);
    const [baseGrade, setBaseGrade] = useState("");
    const [section, setSection] = useState("");
    const [subject, setSubject] = useState("");
    const [editing, setEditing] = useState(null);
    const [uploading, setUploading] = useState(null);
    const [activitiesView, setActivitiesView] = useState(null);
    const [creating, setCreating] = useState(false);
    const [subjectsList, setSubjectsList] = useState([]);

    async function load() {
        const r = await api.get("/lessons");
        const data = r.data || [];
        setAllLessons(data);
        const uniq = Array.from(new Set(data.map((l) => l.subject))).sort();
        setSubjectsList(uniq);
    }
    useEffect(() => { load(); }, []);
    useEffect(() => {
        let filtered = allLessons;
        if (subject) filtered = filtered.filter((l) => l.subject === subject);
        if (baseGrade) filtered = filtered.filter((l) => l.grade && l.grade.startsWith(baseGrade));
        if (section) filtered = filtered.filter((l) => l.grade && l.grade.endsWith(section));
        setLessons(filtered);
    }, [allLessons, baseGrade, section, subject]);

    const availableSections = baseGrade === "5to Secundaria" ? SECTIONS : ["A", "B"];

    return (
        <div data-testid="lessons-tab">
            <div className="flex flex-wrap gap-3 mb-6 items-end justify-between">
                <div className="flex flex-wrap gap-3">
                    <select value={subject} onChange={(e) => setSubject(e.target.value)} className="edu-input">
                        <option value="">Todas las materias</option>
                        {subjectsList.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select value={baseGrade} onChange={(e) => { setBaseGrade(e.target.value); setSection(""); }} className="edu-input">
                        <option value="">Todos los grados</option>
                        {BASE_GRADES.map((g) => <option key={g}>{g}</option>)}
                    </select>
                    <select value={section} onChange={(e) => setSection(e.target.value)} className="edu-input">
                        <option value="">Todas las secciones</option>
                        {(baseGrade ? availableSections : SECTIONS).map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>
                <button
                    onClick={() => setCreating(true)}
                    className="edu-btn-primary px-4 py-2.5 inline-flex items-center gap-2 text-sm"
                >
                    <Plus size={16} /> Nueva Lección
                </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {lessons.length === 0 && <p className="text-edu-muted col-span-2">Sin lecciones aún.</p>}
                {lessons.map((l) => (
                    <div key={l.id} className="edu-card p-6">
                        <div className="flex flex-wrap gap-2 mb-3">
                            <span className="edu-pill bg-edu-navy/10 text-edu-navy">{l.subject}</span>
                            <LearningStyleBadge style={l.learning_style} />
                            <span className="edu-pill bg-gray-100 text-gray-700">{l.grade}</span>
                            {l.unit_number && (
                                <span className="edu-pill bg-edu-gold-soft text-[#0A0A0A]">U{l.unit_number}</span>
                            )}
                            {/* Badge de estado de variantes */}
                            {l.versions_status === "ready" && (
                                <span className="edu-pill bg-emerald-100 text-emerald-800 inline-flex items-center gap-1">
                                    <Sparkles size={10} /> IA lista
                                </span>
                            )}
                            {l.versions_status === "generating" && (
                                <span className="edu-pill bg-yellow-100 text-yellow-800">
                                    Generando IA…
                                </span>
                            )}
                        </div>
                        <h3 className="text-lg font-semibold mb-1">{l.title}</h3>
                        {l.unit_name && <p className="text-xs text-edu-muted mb-2">{l.unit_name}</p>}
                        <p className="text-sm text-edu-body line-clamp-2 mb-4">{l.description}</p>
                        <div className="flex flex-wrap gap-2">
                            <button onClick={() => setEditing(l)} className="px-3 py-1.5 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center gap-1">
                                <Edit2 size={12} /> Editar
                            </button>
                            <button onClick={() => setActivitiesView(l)} className="px-3 py-1.5 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center gap-1">
                                <Eye size={12} /> Ver Actividades
                            </button>
                            <button onClick={() => setUploading(l)} className="px-3 py-1.5 text-xs rounded-lg bg-edu-navy text-white inline-flex items-center gap-1">
                                <Upload size={12} /> Subir Material
                            </button>
                            <button
                                onClick={async () => {
                                    if (!window.confirm(`¿Eliminar "${l.title}"?`)) return;
                                    try {
                                        await api.delete(`/lessons/${l.id}`);
                                        toast.success("Lección eliminada");
                                        load();
                                    } catch (e) {
                                        toast.error(e.response?.data?.detail || "Error eliminando");
                                    }
                                }}
                                className="px-3 py-1.5 text-xs rounded-lg bg-red-100 text-red-700 inline-flex items-center gap-1"
                            >
                                <X size={12} /> Eliminar
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            {editing && <LessonEditModal lesson={editing} onClose={() => setEditing(null)} onSaved={load} />}
            {creating && <LessonCreateModal onClose={() => setCreating(false)} onSaved={load} />}
            {uploading && <UploadModal lesson={uploading} onClose={() => setUploading(null)} onSaved={load} />}
            {activitiesView && <ActivitiesModal lesson={activitiesView} onClose={() => setActivitiesView(null)} />}
        </div>
    );
}

function LessonCreateModal({ onClose, onSaved }) {
    const [form, setForm] = useState({
        subject: "", title: "", description: "", coin_cost: 5,
        learning_style: "visual", grade: GRADES[0],
        unit_number: 1, unit_name: "Unidad 1: ",
        youtube_url: "", learning_outcomes: "",
    });
    const [saving, setSaving] = useState(false);
    const [courses, setCourses] = useState([]);

    useEffect(() => {
        api.get("/courses").then((r) => {
            const list = r.data || [];
            setCourses(list);
            if (list.length > 0 && !form.subject) setForm((f) => ({ ...f, subject: list[0].name }));
        }).catch(() => setCourses([]));
    }, []);

    async function save() {
        if (!form.subject || !form.title || !form.grade || !form.unit_name) {
            toast.error("Materia, título, grado y nombre de unidad son obligatorios");
            return;
        }
        setSaving(true);
        try {
            // 1. Crear la lección
            const { data: newLesson } = await api.post("/lessons", form);
            toast.success("Lección creada");
            onClose();
            onSaved();

            // 2. Disparar generación de variantes en segundo plano (sin bloquear)
            toast.info("Generando versiones personalizadas IA…", { duration: 4000 });
            api.post(`/ai/lessons/${newLesson.id}/generate-versions`)
                .then(() => toast.success("Versiones IA listas para esta lección"))
                .catch(() => toast.error("Error generando versiones IA — podés reintentarlo luego"));

        } catch (e) {
            toast.error(e.response?.data?.detail || "Error");
        } finally {
            setSaving(false);
        }
    }

    return (
        <Modal title="Nueva Lección" onClose={onClose}>
            <div className="space-y-3">
                {courses.length === 0 ? (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-xs text-yellow-800">
                        Primero crea cursos en <strong>Mis Cursos</strong>.
                    </div>
                ) : (
                    <Select label="Materia *" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} options={courses.map((c) => c.name)} />
                )}
                <Input label="Título *" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
                <Textarea label="Descripción" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
                <Select label="Grado y Sección *" value={form.grade} onChange={(v) => setForm({ ...form, grade: v })} options={GRADES} />
                <div className="grid grid-cols-3 gap-3">
                    <Select
                        label="N° Unidad *"
                        value={form.unit_number}
                        onChange={(v) => {
                            const n = parseInt(v, 10);
                            const next = form.unit_name.replace(/^Unidad \d+: /, `Unidad ${n}: `);
                            setForm({ ...form, unit_number: n, unit_name: next });
                        }}
                        options={UNIT_NUMBERS.map(String)}
                    />
                    <div className="col-span-2">
                        <Input label="Nombre de la Unidad *" value={form.unit_name} onChange={(v) => setForm({ ...form, unit_name: v })} />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Input label="Costo (monedas)" type="number" value={form.coin_cost} onChange={(v) => setForm({ ...form, coin_cost: parseInt(v || "0", 10) })} />
                    <Select label="Estilo" value={form.learning_style} onChange={(v) => setForm({ ...form, learning_style: v })} options={Object.keys(STYLE_META)} />
                </div>
                <Input label="YouTube URL" value={form.youtube_url} onChange={(v) => setForm({ ...form, youtube_url: v })} />
                <Textarea label="¿Qué aprenderán?" value={form.learning_outcomes} onChange={(v) => setForm({ ...form, learning_outcomes: v })} />
                <div className="flex justify-end gap-3 pt-3">
                    <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">Cancelar</button>
                    <button onClick={save} disabled={saving || courses.length === 0} className="edu-btn-primary px-4 py-2 text-sm">
                        {saving ? "Guardando…" : "Guardar"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

function LessonEditModal({ lesson, onClose, onSaved }) {
    const [form, setForm] = useState({ ...lesson });
    const [saving, setSaving] = useState(false);
    const [courses, setCourses] = useState([]);

    useEffect(() => {
        api.get("/courses").then((r) => setCourses(r.data || [])).catch(() => setCourses([]));
    }, []);

    async function save() {
        setSaving(true);
        try {
            await api.patch(`/lessons/${lesson.id}`, {
                subject: form.subject, title: form.title, description: form.description,
                coin_cost: form.coin_cost, learning_style: form.learning_style, grade: form.grade,
                unit_number: form.unit_number, unit_name: form.unit_name,
                youtube_url: form.youtube_url, learning_outcomes: form.learning_outcomes,
            });
            toast.success("Actualizado");
            onClose();
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error");
        } finally {
            setSaving(false);
        }
    }

    async function removeFile(field) {
        try {
            await api.patch(`/lessons/${lesson.id}`, { [field]: null });
            setForm((f) => ({ ...f, [field]: null }));
            toast.success("Archivo eliminado");
        } catch (e) {
            toast.error("Error eliminando archivo");
        }
    }

    const FILE_FIELDS = [
        { field: "lesson_plan_file_id", label: "Plan de lección" },
        { field: "whiteboard_file_id", label: "Foto de pizarra" },
        { field: "summary_file_id", label: "Resumen PDF" },
        { field: "activities_file_id", label: "Actividades" },
    ];

    return (
        <Modal title="Editar Lección" onClose={onClose}>
            <div className="space-y-3">
                <Select label="Materia" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} options={courses.length ? courses.map((c) => c.name) : [form.subject]} />
                <Input label="Título" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
                <Textarea label="Descripción" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
                <Select label="Grado y Sección" value={form.grade} onChange={(v) => setForm({ ...form, grade: v })} options={GRADES} />
                <div className="grid grid-cols-3 gap-3">
                    <Select label="N° Unidad" value={form.unit_number} onChange={(v) => setForm({ ...form, unit_number: parseInt(v, 10) })} options={UNIT_NUMBERS.map(String)} />
                    <div className="col-span-2">
                        <Input label="Nombre Unidad" value={form.unit_name || ""} onChange={(v) => setForm({ ...form, unit_name: v })} />
                    </div>
                </div>
                <Textarea label="¿Qué aprenderán?" value={form.learning_outcomes || ""} onChange={(v) => setForm({ ...form, learning_outcomes: v })} />
                <div>
                    <span className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-2 block">Materiales subidos</span>
                    <div className="space-y-2">
                        {FILE_FIELDS.map(({ field, label }) => (
                            <div key={field} className="flex items-center justify-between p-2.5 rounded-xl border border-edu bg-edu-soft">
                                <span className="text-xs text-edu-body">{label}</span>
                                {form[field] ? (
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-emerald-700 font-medium">✓ Subido</span>
                                        <button onClick={() => removeFile(field)} className="text-xs text-red-600 hover:underline">Eliminar</button>
                                    </div>
                                ) : (
                                    <span className="text-xs text-edu-muted">Sin archivo</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <AssignmentsSection lessonId={lesson.id} />

                <div className="flex justify-end gap-3 pt-3">
                    <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">Cancelar</button>
                    <button onClick={save} disabled={saving} className="edu-btn-primary px-4 py-2 text-sm">
                        {saving ? "Guardando…" : "Guardar"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

/* =================== ASSIGNMENTS (Tareas y evaluaciones) =================== */
function AssignmentsSection({ lessonId }) {
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [viewingSubs, setViewingSubs] = useState(null);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get(`/assignments/lesson/${lessonId}`);
            setAssignments(data || []);
        } catch {
            toast.error("Error cargando tareas");
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, [lessonId]);

    async function deleteAssignment(id) {
        if (!window.confirm("¿Eliminar esta tarea? También se eliminarán las entregas de los alumnos.")) return;
        try {
            await api.delete(`/assignments/${id}`);
            toast.success("Tarea eliminada");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error eliminando tarea");
        }
    }

    return (
        <div className="border-t border-edu pt-3 mt-2">
            <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-widest text-edu-muted font-semibold inline-flex items-center gap-1.5">
                    <ClipboardList size={13} /> Tareas y evaluaciones
                </span>
                <button
                    onClick={() => setCreating(true)}
                    className="px-3 py-1 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center gap-1"
                >
                    <Plus size={12} /> Nueva tarea
                </button>
            </div>

            {loading ? (
                <p className="text-xs text-edu-muted">Cargando…</p>
            ) : assignments.length === 0 ? (
                <p className="text-xs text-edu-muted">Sin tareas para esta lección.</p>
            ) : (
                <div className="space-y-2">
                    {assignments.map((a) => (
                        <AssignmentRow
                            key={a.id}
                            assignment={a}
                            onDeleted={() => deleteAssignment(a.id)}
                            onSaved={load}
                            onViewSubs={() => setViewingSubs(a)}
                        />
                    ))}
                </div>
            )}

            {creating && (
                <AssignmentFormModal
                    lessonId={lessonId}
                    onClose={() => setCreating(false)}
                    onSaved={() => { setCreating(false); load(); }}
                />
            )}

            {viewingSubs && (
                <SubmissionsModal
                    assignment={viewingSubs}
                    onClose={() => setViewingSubs(null)}
                />
            )}
        </div>
    );
}

function AssignmentRow({ assignment, onDeleted, onSaved, onViewSubs }) {
    const [editing, setEditing] = useState(false);
    return (
        <div className="bg-edu-soft rounded-xl p-3">
            <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                    <p className="text-sm font-medium">{assignment.title}</p>
                    <p className="text-xs text-edu-muted mt-0.5">
                        {DELIVERY_TYPE_LABELS[assignment.delivery_type] || assignment.delivery_type}
                        {assignment.due_date && ` · Entrega: ${assignment.due_date}`}
                        {" · "}{assignment.max_score} pts
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap justify-end">
                    <button
                        onClick={onViewSubs}
                        className="px-2.5 py-1 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center gap-1"
                    >
                        <Users size={12} /> {assignment.submission_count ?? 0} entregas
                    </button>
                    <button
                        onClick={() => setEditing(true)}
                        className="px-2.5 py-1 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center gap-1"
                    >
                        <Edit2 size={12} /> Editar
                    </button>
                    <button
                        onClick={onDeleted}
                        className="px-2.5 py-1 text-xs rounded-lg bg-red-100 text-red-700 inline-flex items-center gap-1"
                    >
                        <Trash2 size={12} /> Eliminar
                    </button>
                </div>
            </div>

            {editing && (
                <AssignmentFormModal
                    assignment={assignment}
                    onClose={() => setEditing(false)}
                    onSaved={() => { setEditing(false); onSaved(); }}
                />
            )}
        </div>
    );
}

function AssignmentFormModal({ lessonId, assignment, onClose, onSaved }) {
    const isEdit = !!assignment;
    const [form, setForm] = useState(assignment ? {
        title: assignment.title || "",
        instructions: assignment.instructions || "",
        delivery_type: assignment.delivery_type || "pdf",
        due_date: assignment.due_date || "",
        max_score: assignment.max_score ?? 20,
    } : {
        title: "",
        instructions: "",
        delivery_type: "pdf",
        due_date: "",
        max_score: 20,
    });
    const [saving, setSaving] = useState(false);

    async function save() {
        if (!form.title.trim()) {
            toast.error("El título de la tarea es obligatorio");
            return;
        }
        setSaving(true);
        try {
            if (isEdit) {
                await api.patch(`/assignments/${assignment.id}`, {
                    title: form.title,
                    instructions: form.instructions,
                    delivery_type: form.delivery_type,
                    due_date: form.due_date || null,
                    max_score: form.max_score,
                });
                toast.success("Tarea actualizada");
            } else {
                await api.post("/assignments", {
                    lesson_id: lessonId,
                    title: form.title,
                    instructions: form.instructions,
                    delivery_type: form.delivery_type,
                    due_date: form.due_date || null,
                    max_score: form.max_score,
                });
                toast.success("Tarea creada");
            }
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error guardando tarea");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-start justify-center overflow-y-auto" onClick={onClose}>
            <div className="edu-card p-5 max-w-lg w-full my-8" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">{isEdit ? "Editar tarea" : "Nueva tarea"}</h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                <div className="space-y-3">
                    <Input label="Título *" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
                    <Textarea label="Instrucciones" value={form.instructions} onChange={(v) => setForm({ ...form, instructions: v })} />
                    <div className="grid grid-cols-2 gap-3">
                        <Select
                            label="Tipo de entrega"
                            value={form.delivery_type}
                            onChange={(v) => setForm({ ...form, delivery_type: v })}
                            options={[["pdf", "Archivo PDF"], ["photo", "Foto"], ["text", "Texto escrito"]]}
                        />
                        <Input label="Puntaje máximo" type="number" value={form.max_score} onChange={(v) => setForm({ ...form, max_score: parseInt(v || "0", 10) })} />
                    </div>
                    <Input label="Fecha de entrega" type="date" value={form.due_date || ""} onChange={(v) => setForm({ ...form, due_date: v })} />
                    <div className="flex justify-end gap-3 pt-2">
                        <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">Cancelar</button>
                        <button onClick={save} disabled={saving} className="edu-btn-primary px-4 py-2 text-sm">
                            {saving ? "Guardando…" : "Guardar"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function SubmissionsModal({ assignment, onClose }) {
    const [subs, setSubs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/assignments/${assignment.id}/submissions`)
            .then((r) => setSubs(r.data || []))
            .catch(() => toast.error("Error cargando entregas"))
            .finally(() => setLoading(false));
    }, [assignment.id]);

    return (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-start justify-center overflow-y-auto" onClick={onClose}>
            <div className="edu-card p-5 max-w-lg w-full my-8" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-semibold">{assignment.title}</h3>
                        <p className="text-xs text-edu-muted">Entregas de los alumnos</p>
                    </div>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                {loading ? (
                    <p className="text-xs text-edu-muted">Cargando…</p>
                ) : subs.length === 0 ? (
                    <p className="text-xs text-edu-muted">Nadie ha entregado esta tarea aún.</p>
                ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto edu-scroll">
                        {subs.map((s) => (
                            <div key={s.id} className="flex items-center justify-between p-3 rounded-xl bg-edu-soft">
                                <div>
                                    <p className="text-sm font-medium">{s.student_name}</p>
                                    <p className="text-xs text-edu-muted">{new Date(s.submitted_at).toLocaleString()}</p>
                                </div>
                                {s.delivery_type === "text" ? (
                                    <span className="text-xs text-edu-muted">Texto entregado</span>
                                ) : s.file_id ? (
                                    <a
                                        href={`/api/files/${s.file_id}/download`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="px-3 py-1 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center gap-1"
                                    >
                                        <Eye size={12} /> Ver entrega
                                    </a>
                                ) : null}
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex justify-end pt-4">
                    <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">Cerrar</button>
                </div>
            </div>
        </div>
    );
}

/* =================== UNITS TAB =================== */
function UnitsTab() {
    const [units, setUnits] = useState([]);
    const [allUnits, setAllUnits] = useState([]);
    const [baseGrade, setBaseGrade] = useState("");
    const [section, setSection] = useState("");

    async function load() {
        const r = await api.get("/lessons/units");
        setAllUnits(r.data || []);
    }
    useEffect(() => { load(); }, []);
    useEffect(() => {
        let filtered = allUnits;
        if (baseGrade) filtered = filtered.filter((u) => u.grade && u.grade.startsWith(baseGrade));
        if (section) filtered = filtered.filter((u) => u.grade && u.grade.endsWith(section));
        setUnits(filtered);
    }, [allUnits, baseGrade, section]);

    async function toggle(u) {
        const action = u.active ? "deactivate" : "activate";
        await api.post(`/lessons/units/${u.id}/${action}`);
        toast.success(u.active ? "Unidad desactivada" : "Unidad activada");
        load();
    }

    const availableSections = baseGrade === "5to Secundaria" ? SECTIONS : ["A", "B"];

    return (
        <div data-testid="units-tab">
            <div className="flex flex-wrap items-center gap-3 mb-6">
                <select value={baseGrade} onChange={(e) => { setBaseGrade(e.target.value); setSection(""); }} className="edu-input">
                    <option value="">Todos los grados</option>
                    {BASE_GRADES.map((g) => <option key={g}>{g}</option>)}
                </select>
                <select value={section} onChange={(e) => setSection(e.target.value)} className="edu-input">
                    <option value="">Todas las secciones</option>
                    {(baseGrade ? availableSections : SECTIONS).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {units.length === 0 && <p className="text-edu-muted col-span-full">No hay unidades aún.</p>}
                {units.map((u) => (
                    <div key={u.id} className="edu-card p-5">
                        <div className="flex items-start justify-between mb-2">
                            <div>
                                <span className="edu-pill bg-edu-navy/10 text-edu-navy">{u.subject}</span>
                                <h3 className="text-base font-semibold mt-2">U{u.unit_number}: {u.unit_name}</h3>
                                <p className="text-xs text-edu-muted">{u.grade}</p>
                            </div>
                            <span className={`edu-pill ${u.active ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-600"}`}>
                                {u.active ? "Activa" : "Bloqueada"}
                            </span>
                        </div>
                        <p className="text-sm text-edu-body mt-3">{u.lesson_count} lecciones</p>
                        <button
                            onClick={() => toggle(u)}
                            className={`mt-3 w-full px-3 py-2 text-xs rounded-lg inline-flex items-center justify-center gap-1.5 ${
                                u.active ? "bg-gray-100 text-gray-700 hover:bg-gray-200" : "bg-edu-navy text-white hover:opacity-90"
                            }`}
                        >
                            {u.active ? <Lock size={12} /> : <Unlock size={12} />}
                            {u.active ? "Bloquear" : "Activar"}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* =================== EXAMS TAB =================== */
function ExamsTab() {
    const [exams, setExams] = useState([]);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);
    const [filter, setFilter] = useState("all");

    async function load() { const r = await api.get("/exams"); setExams(r.data || []); }
    useEffect(() => { load(); }, []);

    async function approve(id) { await api.post(`/exams/${id}/approve`); toast.success("Examen aprobado"); load(); }
    async function unapprove(id) { await api.post(`/exams/${id}/unapprove`); toast.success("Examen ocultado"); load(); }

    const filtered = exams.filter((e) => {
        if (filter === "pending") return !e.approved;
        if (filter === "approved") return e.approved;
        return true;
    });

    return (
        <div data-testid="exams-tab">
            <div className="flex flex-wrap items-center gap-3 mb-6 justify-between">
                <div className="flex gap-2">
                    {[["all", "Todos"], ["pending", "Pendientes"], ["approved", "Aprobados"]].map(([v, l]) => (
                        <button key={v} onClick={() => setFilter(v)} className={`px-3 py-1.5 rounded-xl text-xs ${filter === v ? "bg-edu-navy text-white" : "border border-edu hover:bg-gray-50"}`}>{l}</button>
                    ))}
                </div>
                <button onClick={() => setCreating(true)} className="edu-btn-primary px-4 py-2 inline-flex items-center gap-2 text-sm">
                    <Plus size={14} /> Generar Examen con IA
                </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {filtered.length === 0 && <p className="text-edu-muted col-span-2">Sin exámenes.</p>}
                {filtered.map((e) => (
                    <div key={e.id} className="edu-card p-5">
                        <div className="flex items-start justify-between mb-2">
                            <div>
                                <span className="edu-pill bg-edu-navy/10 text-edu-navy">{e.subject}</span>
                                {e.unit_number && <span className="edu-pill bg-edu-gold-soft text-[#0A0A0A] ml-1">U{e.unit_number}</span>}
                                <h3 className="text-base font-semibold mt-2">{e.title}</h3>
                                <p className="text-xs text-edu-muted">{e.grade} · {e.questions?.length || 0} preguntas</p>
                            </div>
                            <span className={`edu-pill ${e.approved ? "bg-emerald-100 text-emerald-800" : "bg-yellow-100 text-yellow-800"}`}>
                                {e.approved ? "Aprobado" : "Pendiente"}
                            </span>
                        </div>
                        {e.ai_generated && !e.approved && (
                            <p className="text-xs text-yellow-800 mt-2 inline-flex items-center gap-1">
                                <AlertCircle size={12} /> Generado por IA - revisa antes de aprobar
                            </p>
                        )}
                        <div className="flex flex-wrap gap-2 mt-3">
                            <button onClick={() => setEditing(e)} className="px-3 py-1.5 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center gap-1">
                                <Edit2 size={12} /> Editar
                            </button>
                            {e.approved ? (
                                <button onClick={() => unapprove(e.id)} className="px-3 py-1.5 text-xs rounded-lg bg-gray-100 text-gray-700">Desaprobar</button>
                            ) : (
                                <button onClick={() => approve(e.id)} className="px-3 py-1.5 text-xs rounded-lg bg-emerald-600 text-white inline-flex items-center gap-1">
                                    <CheckCircle2 size={12} /> Aprobar
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            {creating && <ExamCreateModal onClose={() => setCreating(false)} onSaved={load} />}
            {editing && <ExamEditInlineModal exam={editing} onClose={() => setEditing(null)} onSaved={load} />}
        </div>
    );
}

function ExamEditInlineModal({ exam, onClose, onSaved }) {
    const [questions, setQuestions] = useState(exam.questions || []);
    const [title, setTitle] = useState(exam.title || "");
    const [duration, setDuration] = useState(exam.duration_minutes || 30);
    const [coinCost, setCoinCost] = useState(exam.coin_cost || 10);
    const [saving, setSaving] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const [topic, setTopic] = useState(exam.title || "");
    const [count, setCount] = useState(5);

    function update(i, field, value) { const next = [...questions]; next[i] = { ...next[i], [field]: value }; setQuestions(next); }

    async function save() {
        setSaving(true);
        try {
            await api.patch(`/exams/${exam.id}`, { title, duration_minutes: duration, coin_cost: coinCost, questions });
            toast.success("Examen actualizado"); onClose(); onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
        finally { setSaving(false); }
    }

    async function regenerate() {
        setRegenerating(true);
        try {
            const { data } = await api.post(`/exams/${exam.id}/regenerate`, { topic, count });
            setQuestions(data.questions); toast.success("Preguntas regeneradas");
        } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
        finally { setRegenerating(false); }
    }

    return (
        <Modal title={`Editar: ${exam.title}`} onClose={onClose}>
            <div className="space-y-3">
                <Input label="Título" value={title} onChange={setTitle} />
                <div className="grid grid-cols-2 gap-3">
                    <Input label="Duración (min)" type="number" value={duration} onChange={(v) => setDuration(parseInt(v || "0", 10))} />
                    <Input label="Costo (monedas)" type="number" value={coinCost} onChange={(v) => setCoinCost(parseInt(v || "0", 10))} />
                </div>
                <div className="p-3 bg-edu-gold-soft border border-edu-gold/30 rounded-xl">
                    <div className="text-xs font-semibold mb-2 inline-flex items-center gap-1"><Sparkles size={12} className="text-edu-gold" /> Regenerar con IA</div>
                    <div className="grid grid-cols-3 gap-2">
                        <input value={topic} onChange={(ev) => setTopic(ev.target.value)} placeholder="Tema" className="edu-input col-span-2 text-sm py-2" />
                        <input type="number" value={count} onChange={(ev) => setCount(parseInt(ev.target.value || "5", 10))} min={1} max={20} className="edu-input text-sm py-2" />
                    </div>
                    <button onClick={regenerate} disabled={regenerating || !topic.trim()} className="mt-2 edu-btn-accent px-3 py-1.5 text-xs disabled:opacity-50">
                        {regenerating ? "Regenerando…" : "Regenerar"}
                    </button>
                </div>
                <div className="space-y-2 max-h-72 overflow-y-auto edu-scroll pr-1">
                    {questions.map((q, i) => (
                        <div key={i} className="border border-edu rounded-xl p-2 space-y-1.5">
                            <input value={q.question} onChange={(ev) => update(i, "question", ev.target.value)} className="edu-input w-full text-sm py-1.5" />
                            {q.options.map((opt, oi) => (
                                <div key={oi} className="flex items-center gap-2">
                                    <input type="radio" checked={q.correct_index === oi} onChange={() => update(i, "correct_index", oi)} />
                                    <input value={opt} onChange={(ev) => { const opts = [...q.options]; opts[oi] = ev.target.value; update(i, "options", opts); }} className="edu-input flex-1 text-sm py-1.5" />
                                </div>
                            ))}
                            <button onClick={() => setQuestions(questions.filter((_, k) => k !== i))} className="text-xs text-red-600">Eliminar</button>
                        </div>
                    ))}
                    <button onClick={() => setQuestions([...questions, { question: "", options: ["", "", "", ""], correct_index: 0 }])} className="text-sm text-edu-navy hover:underline">+ Agregar pregunta</button>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                    <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">Cancelar</button>
                    <button onClick={save} disabled={saving} className="edu-btn-primary px-4 py-2 text-sm">{saving ? "Guardando…" : "Guardar"}</button>
                </div>
            </div>
        </Modal>
    );
}

function ExamCreateModal({ onClose, onSaved }) {
    const [units, setUnits] = useState([]);
    const [form, setForm] = useState({
        subject: "", title: "", grade: GRADES[0], unit_number: 1,
        unit_name: "", duration_minutes: 20, coin_cost: 10, topic: "",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!form.grade) return;
        api.get("/lessons/units", { params: { grade: form.grade } }).then((r) => setUnits(r.data || []));
    }, [form.grade]);

    function pickUnit(u) {
        setForm({ ...form, subject: u.subject, unit_number: u.unit_number, unit_name: u.unit_name, title: `Examen Unidad ${u.unit_number} - ${u.subject}` });
    }

    async function save() {
        if (!form.subject || !form.title || !form.unit_number) { toast.error("Selecciona materia, título y unidad"); return; }
        setSaving(true);
        try {
            await api.post("/exams", form);
            toast.success("Examen generado. Revísalo y apruébalo."); onClose(); onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
        finally { setSaving(false); }
    }

    return (
        <Modal title="Generar Examen con IA" onClose={onClose}>
            <div className="space-y-3">
                <p className="text-xs text-edu-body bg-edu-gold-soft border border-edu-gold/30 rounded-xl p-3 inline-flex items-start gap-2">
                    <Sparkles size={14} className="text-edu-gold mt-0.5 flex-shrink-0" />
                    <span>La IA generará preguntas usando el contenido de la unidad seleccionada.</span>
                </p>
                <Select label="Grado y Sección" value={form.grade} onChange={(v) => setForm({ ...form, grade: v })} options={GRADES} />
                <div>
                    <span className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-1.5 block">Unidad de origen *</span>
                    {units.length === 0 ? (
                        <p className="text-xs text-edu-muted">No hay unidades para este grado.</p>
                    ) : (
                        <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto edu-scroll">
                            {units.map((u) => (
                                <button key={u.id} type="button" onClick={() => pickUnit(u)}
                                    className={`text-left p-2.5 rounded-lg border text-sm ${form.subject === u.subject && form.unit_number === u.unit_number ? "border-edu-navy bg-edu-navy/5" : "border-edu hover:bg-gray-50"}`}>
                                    <div className="font-medium">U{u.unit_number}: {u.unit_name}</div>
                                    <div className="text-xs text-edu-muted">{u.subject} · {u.lesson_count} lecciones</div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <Input label="Título" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
                <Input label="Tema (opcional)" value={form.topic} onChange={(v) => setForm({ ...form, topic: v })} />
                <div className="grid grid-cols-2 gap-3">
                    <Input label="Duración (min)" type="number" value={form.duration_minutes} onChange={(v) => setForm({ ...form, duration_minutes: parseInt(v || "0", 10) })} />
                    <Input label="Costo (monedas)" type="number" value={form.coin_cost} onChange={(v) => setForm({ ...form, coin_cost: parseInt(v || "0", 10) })} />
                </div>
                <div className="flex justify-end gap-3 pt-3">
                    <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">Cancelar</button>
                    <button onClick={save} disabled={saving} className="edu-btn-primary px-4 py-2 text-sm">{saving ? "Generando…" : "Generar con IA"}</button>
                </div>
            </div>
        </Modal>
    );
}

/* =================== Reusable =================== */
function Modal({ title, onClose, children }) {
    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="edu-card p-6 max-w-md w-full max-h-[90vh] overflow-y-auto edu-scroll">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold">{title}</h3>
                    <button onClick={onClose} className="text-edu-muted hover:text-edu-navy"><X size={18} /></button>
                </div>
                {children}
            </div>
        </div>
    );
}

function Input({ label, value, onChange, type = "text" }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-1.5 block">{label}</span>
            <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="edu-input w-full" />
        </label>
    );
}

function Textarea({ label, value, onChange }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-1.5 block">{label}</span>
            <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} className="edu-input w-full" />
        </label>
    );
}

function Select({ label, value, onChange, options, testId }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-1.5 block">{label}</span>
            <select value={value} onChange={(e) => onChange(e.target.value)} className="edu-input w-full" data-testid={testId}>
                {options.map((o) => { const [v, l] = Array.isArray(o) ? o : [o, o]; return <option key={v} value={v}>{l}</option>; })}
            </select>
        </label>
    );
}

/* =================== UploadModal / ActivitiesModal (unchanged stubs preserved) =================== */
function UploadModal({ lesson, onClose, onSaved }) {
    const [uploading, setUploading] = useState(false);
    async function upload(field, file) {
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.patch(`/lessons/${lesson.id}`, { [field]: data.id });
            toast.success("Material subido");
            onSaved();
        } catch {
            toast.error("Error al subir el archivo");
        } finally {
            setUploading(false);
        }
    }

    const FIELDS = [
        { field: "summary_file_id", label: "Resumen PDF", accept: ".pdf" },
        { field: "whiteboard_file_id", label: "Foto de pizarra", accept: "image/*" },
        { field: "activities_file_id", label: "Actividades", accept: ".pdf,.doc,.docx" },
        { field: "lesson_plan_file_id", label: "Plan de lección", accept: ".pdf" },
    ];

    return (
        <Modal title={`Subir material — ${lesson.title}`} onClose={onClose}>
            <div className="space-y-2">
                {FIELDS.map(({ field, label, accept }) => (
                    <label key={field} className="flex items-center justify-between p-3 rounded-xl border border-edu bg-edu-soft cursor-pointer hover:bg-gray-50">
                        <span className="text-sm">{label}</span>
                        <span className="px-3 py-1 text-xs rounded-lg bg-edu-navy text-white inline-flex items-center gap-1">
                            <Upload size={12} /> {uploading ? "Subiendo…" : "Subir"}
                        </span>
                        <input type="file" accept={accept} className="hidden" disabled={uploading}
                            onChange={(e) => e.target.files[0] && upload(field, e.target.files[0])} />
                    </label>
                ))}
            </div>
            <div className="flex justify-end pt-4">
                <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">Cerrar</button>
            </div>
        </Modal>
    );
}

function ActivitiesModal({ lesson, onClose }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get(`/lessons/${lesson.id}/activities`)
            .then((r) => setRows(r.data || []))
            .catch(() => toast.error("Error cargando actividades"))
            .finally(() => setLoading(false));
    }, [lesson.id]);

    return (
        <Modal title={`Actividades — ${lesson.title}`} onClose={onClose}>
            {loading ? (
                <p className="text-xs text-edu-muted">Cargando…</p>
            ) : rows.length === 0 ? (
                <p className="text-xs text-edu-muted">Nadie ha completado esta lección aún.</p>
            ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto edu-scroll">
                    {rows.map((r, i) => (
                        <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-edu-soft">
                            <span className="text-sm">{r.student_name}</span>
                            <span className="text-xs text-edu-muted">{new Date(r.completed_at).toLocaleString()}</span>
                        </div>
                    ))}
                </div>
            )}
            <div className="flex justify-end pt-4">
                <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">Cerrar</button>
            </div>
        </Modal>
    );
}
